import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";

export const listAccountsAction: Action = {
    name: "LIST_ACCOUNTS",
    similes: [
        "SHOW_ACCOUNTS",
        "GET_ACCOUNTS",
        "LIST_WALLETS",
        "SHOW_WALLETS",
        "MY_ACCOUNTS"
    ],
    description: "List all configured COTI accounts and their information",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            // This action is always valid if we can initialize the wallet provider
            const walletProvider = initCotiWalletProvider(runtime);
            return walletProvider.getAddress() !== "";
        } catch {
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting LIST_ACCOUNTS action");

            const walletProvider = initCotiWalletProvider(runtime);
            const address = walletProvider.getAddress();
            const balance = await walletProvider.getBalance();
            
            // Get additional account information
            const provider = walletProvider.getProvider();
            const transactionCount = await provider.getTransactionCount(address);
            const network = await provider.getNetwork();

            const accountInfo = {
                address,
                balance: ethers.utils.formatEther(balance),
                balanceWei: balance.toString(),
                transactionCount,
                network: {
                    name: network.name,
                    chainId: network.chainId.toString(),
                },
            };

            const responseText = `COTI Account Information:
Address: ${address}
Balance: ${ethers.utils.formatEther(balance)} COTI
Transaction Count: ${transactionCount}
Network: ${network.name} (Chain ID: ${network.chainId})`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        accounts: [accountInfo],
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    accounts: [accountInfo],
                    totalAccounts: 1,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in LIST_ACCOUNTS action:", error);
            const errorMessage = `Failed to list COTI accounts: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Show me my COTI accounts",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll list all your configured COTI accounts.",
                    action: "LIST_ACCOUNTS",
                },
            },
        ],
        [
            {
                name: "{{user1}}",
                content: {
                    text: "What accounts do I have?",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "Let me show you your COTI account information.",
                    action: "LIST_ACCOUNTS",
                },
            },
        ],
    ],
};
